/**
 * Amazon Advertising Adapter (Story 19.13 — OAuth scaffold only)
 *
 * Plain object implementing IntegrationAdapter — no class, no inheritance.
 *
 * Story 19.13 wires OAuth + multi-region profile discovery + token refresh +
 * connection validation. **Stories 19.14 (Sponsored Products), 19.15 (SB+SD),
 * 19.16 (DSP), 19.17 (presets+fixtures) ship the metric pipeline** — replacing
 * the deferred `invalid_response` `fetchMetrics()` stub with the full async
 * report-job + S3 download + parse pipeline (491+ metrics / 34 dimensions
 * across SP/SB/SD/DSP).
 *
 * Amazon-specific characteristics distinguishing this adapter:
 *   - **HTTP Basic Authentication** on token endpoint (mirrors Reddit / Pinterest)
 *   - **DOUBLE-COLON scope** (`advertising::campaign_management`)
 *   - **Refresh tokens DO NOT rotate** — refresh response omits the field
 *     (mirrors Reddit 19.10 non-rotating pattern, inverts Snapchat 19.7)
 *   - **Region-partitioned profiles** (NA / EU / FE) — discovery requires 3
 *     sequential `/v2/profiles` calls; `validateConnection()` re-runs full
 *     enumeration to verify the LWA grant still has at least one accessible
 *     profile across any region.
 *   - **60-minute access TTL** (TD4 → inlineRefreshOn401: true REQUIRED)
 *   - **180-day client-secret rotation** at the LWA app level — refresh
 *     tokens survive rotation as long as `client_id` is unchanged.
 *   - **`invalid_grant` → `token_expired`**, **`invalid_client` →
 *     `deployment_misconfig`** (NOT `token_expired`) — AC #15 highest-blast-
 *     radius invariant.
 *
 * ADRs:
 *   - ADR-013 (Adapter Pattern): plain object implementing IntegrationAdapter
 *   - ADR-014 (API Versioning): `amazonAds` lives in `API_VERSIONS`
 *   - ADR-016 (Aggressive Caching): `PLATFORM_CACHE_TTL["amazon-ads"] = 3600`
 *   - ADR-017 (Retry Logic): all HTTP calls flow through `safeFetch`
 *   - ADR-020 (Token Lifecycle): LWA refresh tokens permanent + non-rotating
 */

import type {
  AccountInfo,
  AdapterContext,
  AdapterErrorCode,
  AdapterMetrics,
  AdapterResult,
  DimensionDefinition,
  IntegrationAdapter,
  MetricDefinition,
  OAuthCredentials,
  RefreshedTokens,
} from "../adapter";
import { adapterErr, adapterOk } from "../adapter";
import { API_VERSIONS } from "../config";
import {
  fetchAmazonAdsProfilesAcrossRegions,
} from "./api";
import {
  AmazonAdsApiCallError,
  amazonAdsErrorTypeToAdapterCode,
} from "./errors";
import {
  AmazonAdsTokenError,
  refreshAmazonAdsToken,
  sanitizeAmazonAdsErrorMessage,
  type AmazonAdsRegion,
} from "./oauth";
import type { AdapterRefreshContext } from "../oauth-families";
import { runSpFetchMetrics } from "./sp-fetch-metrics";
import { runSbFetchMetrics } from "./sb-fetch-metrics";
import { runSdFetchMetrics } from "./sd-fetch-metrics";
import { runDspFetchMetrics } from "./dsp-fetch-metrics";
import { buildSpReportJobsApi, buildSpKvApi } from "./sp-report-jobs-d1";
import {
  AMAZON_ADS_SP_DIMENSIONS,
  AMAZON_ADS_SP_METRICS,
  SP_V3_COLUMN_MAP,
} from "./sp-column-map";
import {
  AMAZON_ADS_SB_DIMENSIONS,
  AMAZON_ADS_SB_METRICS,
  SB_V3_COLUMN_MAP,
} from "./sb-column-map";
import {
  AMAZON_ADS_SD_DIMENSIONS,
  AMAZON_ADS_SD_METRICS,
  SD_V3_COLUMN_MAP,
} from "./sd-column-map";
import {
  AMAZON_ADS_DSP_DIMENSIONS,
  AMAZON_ADS_DSP_METRICS,
  DSP_V3_COLUMN_MAP,
  registerDspExtendedColumns,
  type DspExtendedColumnSource,
  type DspReportTypeId,
} from "./dsp-column-map";
// Story 19.16.1 — additional DSP column maps + Stores Analytics surface.
import {
  DSP_FUNNEL_COLUMN_MAP,
  emitDspFunnelEventAuditLog,
} from "./dsp-funnel-events-column-map";
import { DSP_MASHUP_COLUMN_MAP } from "./dsp-mashup-column-map";
import { DSP_TECHNICAL_COLUMN_MAP } from "./dsp-technical-column-map";
import {
  AMAZON_ADS_DSP_AUDIENCE_FLOOR_METRICS,
} from "./dsp-audience-introspection";
import {
  AMAZON_ADS_DSP_AUDIO_VIDEO_FLOOR_METRICS,
} from "./dsp-audio-video-introspection";
import {
  AMAZON_ADS_STORE_ANALYTICS_DIMENSIONS,
  AMAZON_ADS_STORE_ANALYTICS_METRICS,
  emitBseGridAuditLog,
  STORE_ANALYTICS_SECTION_LEVEL_COLUMN_MAP,
} from "./store-analytics-column-map";
import {
  buildAmazonAdsReportRequest,
  buildSpReportRequest,
  cancelReport as cancelReportApi,
  submitAmazonAdsReport,
  submitSponsoredProductsReport,
  parseAmazonAdsReportApiError,
  SpReportApiError,
  type AmazonAdsAdProduct,
} from "./reports";

// ============================================================================
// Constants
// ============================================================================

/** API version is exported for parity with other adapters' module-level constants. */
export const AMAZON_ADS_API_VERSION = API_VERSIONS.amazonAds;

/**
 * Story 19.15 — 84 priority metrics (30 SP + 24 SB + 30 SD) covering the
 * agency-reporting + branded-reporting + view-through-retargeting golden paths.
 * DSP (~341 metrics) lands in Story 19.16; presets + fixtures across all 4
 * surfaces in 19.17.
 *
 * Per AC #14.8 — every metric ID is unique across SP/SB/SD via prefixed keys
 * (sp/sb/sd) for cross-product lookups, while preserving the original
 * lowerCamelCase keys within each adProduct's column map for direct API field
 * matching. The `id` field used by widget routing is `amazon-ads-{slug}-{key}`.
 */
function tagPlatformMetrics(
  metrics: MetricDefinition[],
  slug: "sp" | "sb" | "sd" | "dsp"
): MetricDefinition[] {
  return metrics.map((m) => ({
    ...m,
    // Preserve the API field name for the column map; the `key` continues to
    // be the lowerCamelCase identifier (matches Amazon's column name) so
    // existing 19.14 SP tests continue to pass.
    key: m.key,
    // Story 19.15 AC #14.8 / Story 19.16 — cross-product metric ID uniqueness
    // via category tagging. Widget code keys on `apiField` (the wire name) to
    // address metrics; the `category` field surfaces "Amazon Ads SP" / "SB" /
    // "SD" / "DSP" so the picker UI can group them.
    category: `Amazon Ads ${slug.toUpperCase()}`,
  }));
}

const AMAZON_ADS_SP_TAGGED = tagPlatformMetrics(AMAZON_ADS_SP_METRICS, "sp");
const AMAZON_ADS_SB_TAGGED = tagPlatformMetrics(AMAZON_ADS_SB_METRICS, "sb");
const AMAZON_ADS_SD_TAGGED = tagPlatformMetrics(AMAZON_ADS_SD_METRICS, "sd");
const AMAZON_ADS_DSP_TAGGED = tagPlatformMetrics(AMAZON_ADS_DSP_METRICS, "dsp");

// Story 19.16.1 — synthesize MetricDefinition arrays from the new column maps.
function synthesizeFromDspColumnMap(
  map: Record<
    string,
    {
      key: string;
      label: string;
      columnName: string;
      dataType: string;
      compatibleWidgets: readonly string[];
      aggregationKind?: MetricDefinition["aggregationKind"];
      ratioComponents?: MetricDefinition["ratioComponents"];
    }
  >
): MetricDefinition[] {
  return Object.values(map).map((m) => ({
    key: m.key,
    label: m.label,
    type:
      m.dataType === "Currency"
        ? "currency"
        : m.dataType === "Percentage"
          ? "percentage"
          : "number",
    category: "Amazon Ads DSP",
    dataType:
      m.dataType === "String"
        ? "Enum"
        : m.dataType === "Decimal"
          ? "Decimal"
          : (m.dataType as "Integer" | "Currency" | "Percentage" | "Ratio"),
    // Story 31-31 — carry aggregation metadata through the projection
    // (String→Enum entries are exempt and never annotated at the source).
    ...(m.aggregationKind !== undefined ? { aggregationKind: m.aggregationKind } : {}),
    ...(m.ratioComponents !== undefined ? { ratioComponents: m.ratioComponents } : {}),
    compatibleWidgets: [...m.compatibleWidgets],
    apiField: m.columnName,
  }));
}

const AMAZON_ADS_DSP_FUNNEL_TAGGED = tagPlatformMetrics(
  synthesizeFromDspColumnMap(DSP_FUNNEL_COLUMN_MAP),
  "dsp"
);
const AMAZON_ADS_DSP_MASHUP_TAGGED = tagPlatformMetrics(
  synthesizeFromDspColumnMap(DSP_MASHUP_COLUMN_MAP),
  "dsp"
);
const AMAZON_ADS_DSP_TECHNICAL_TAGGED = tagPlatformMetrics(
  synthesizeFromDspColumnMap(DSP_TECHNICAL_COLUMN_MAP),
  "dsp"
);

// Story 19.16.1 — runtime-introspected AUDIENCE + AUDIO_VIDEO floors as
// MetricDefinitions so they appear in `getAvailableMetrics`. Per-tenant
// runtime extensions are NOT pre-listed (caller introspects on first fetch).
//
// Labels are user-facing; we capitalize the canonical agency-facing label
// and tag the surface in parentheses so the picker UI can group cleanly.
const AUDIENCE_LABELS: Record<string, string> = {
  spend: "Spend",
  impressions: "Impressions",
  clicks: "Clicks",
  brandedSearches: "Branded Searches",
  detailPageViews: "Detail Page Views",
  addToCart: "Add to Cart",
  purchases: "Purchases",
};
const AMAZON_ADS_DSP_AUDIENCE_FLOOR_TAGGED: MetricDefinition[] = (
  AMAZON_ADS_DSP_AUDIENCE_FLOOR_METRICS as readonly string[]
).map((key) => ({
  key,
  label: `${AUDIENCE_LABELS[key] ?? key} (AUDIENCE)`,
  type: key === "spend" ? "currency" : "number",
  category: "Amazon Ads DSP",
  dataType: key === "spend" ? "Currency" : "Integer",
  // Story 31-31 — every AUDIENCE floor metric is a spend/event count → additive.
  aggregationKind: "additive" as const,
  compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  apiField: key,
  id: `amazon-ads-dsp-${key}`,
}));

const AUDIO_VIDEO_LABELS: Record<string, string> = {
  audioImpressions: "Audio Impressions",
  audioCompleteListens: "Audio Complete Listens",
  videoCTVImpressions: "Video CTV Impressions",
  videoCTVCompletes: "Video CTV Completes",
};
const AMAZON_ADS_DSP_AUDIO_VIDEO_FLOOR_TAGGED: MetricDefinition[] = (
  AMAZON_ADS_DSP_AUDIO_VIDEO_FLOOR_METRICS as readonly string[]
).map((key) => ({
  key,
  label: `${AUDIO_VIDEO_LABELS[key] ?? key} (AUDIO_VIDEO BETA)`,
  type: "number",
  category: "Amazon Ads DSP",
  dataType: "Integer",
  // Story 31-31 — impressions/completes are event counts → additive.
  aggregationKind: "additive" as const,
  compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  apiField: key,
  id: `amazon-ads-dsp-${key}`,
}));

// Story 19.16.1 — Stores Analytics ships under category "Amazon Brand Stores"
// (set in `store-analytics-column-map.ts`); we do NOT pass it through
// `tagPlatformMetrics` because it overrides the category. Stores Analytics
// metrics live on a SEPARATE Amazon endpoint, so they get their own category.
const AMAZON_ADS_STORE_ANALYTICS_TAGGED = AMAZON_ADS_STORE_ANALYTICS_METRICS;

// Story 19.16.1 — register all extended DSP column sources with
// `buildDspColumnList` so callers passing funnel/mashup/technical/
// AUDIENCE-floor/AUDIO_VIDEO-floor metric keys actually get those columns
// included in the request body (rather than silently dropped).
const EXTENDED_DSP_SOURCES: DspExtendedColumnSource[] = [];
for (const m of Object.values(DSP_FUNNEL_COLUMN_MAP)) {
  EXTENDED_DSP_SOURCES.push({
    metricKey: m.key,
    reportTypeId: m.reportTypeId as DspReportTypeId,
    columnName: m.columnName,
  });
}
for (const m of Object.values(DSP_MASHUP_COLUMN_MAP)) {
  EXTENDED_DSP_SOURCES.push({
    metricKey: m.key,
    reportTypeId: m.reportTypeId as DspReportTypeId,
    columnName: m.columnName,
  });
}
for (const m of Object.values(DSP_TECHNICAL_COLUMN_MAP)) {
  EXTENDED_DSP_SOURCES.push({
    metricKey: m.key,
    reportTypeId: m.reportTypeId as DspReportTypeId,
    columnName: m.columnName,
  });
}
for (const k of AMAZON_ADS_DSP_AUDIENCE_FLOOR_METRICS) {
  EXTENDED_DSP_SOURCES.push({
    metricKey: k,
    reportTypeId: "DSP_AUDIENCE",
    columnName: k,
  });
}
for (const k of AMAZON_ADS_DSP_AUDIO_VIDEO_FLOOR_METRICS) {
  EXTENDED_DSP_SOURCES.push({
    metricKey: k,
    reportTypeId: "DSP_AUDIO_VIDEO",
    columnName: k,
  });
}
// Story 19.16.1 AC #11 — register the 6 DSP `*AllViews*` baseline columns as
// extended sources on DSP_CAMPAIGN so cross-2026-boundary requests inject
// them as recognized columns rather than warnings.
const DSP_ALLVIEWS_COLUMNS = [
  "purchasesAllViews14d",
  "salesAllViews14d",
  "totalPurchasesAllViews14d",
  "totalSalesAllViews14d",
  "newToBrandPurchasesAllViews14d",
  "newToBrandSalesAllViews14d",
];
for (const k of DSP_ALLVIEWS_COLUMNS) {
  EXTENDED_DSP_SOURCES.push({
    metricKey: k,
    reportTypeId: "DSP_CAMPAIGN",
    columnName: k,
  });
}
registerDspExtendedColumns(EXTENDED_DSP_SOURCES);

export const AMAZON_ADS_METRICS: MetricDefinition[] = [
  ...AMAZON_ADS_SP_TAGGED,
  ...AMAZON_ADS_SB_TAGGED,
  ...AMAZON_ADS_SD_TAGGED,
  ...AMAZON_ADS_DSP_TAGGED,
  // Story 19.16.1 — extended DSP column maps + STORE_ANALYTICS.
  ...AMAZON_ADS_DSP_FUNNEL_TAGGED,
  ...AMAZON_ADS_DSP_MASHUP_TAGGED,
  ...AMAZON_ADS_DSP_TECHNICAL_TAGGED,
  ...AMAZON_ADS_DSP_AUDIENCE_FLOOR_TAGGED,
  ...AMAZON_ADS_DSP_AUDIO_VIDEO_FLOOR_TAGGED,
  ...AMAZON_ADS_STORE_ANALYTICS_TAGGED,
];

/**
 * Story 35.7 AC #7 — 18 NEW dimension keys (Portfolio + Campaign Budget
 * cluster of 6, Keyword family of 9, Lead Gen-specific of 3). All routed
 * through existing per-product dimension maps so `buildColumnList` /
 * `buildSdColumnList` can resolve them to wire-format column names
 * (Code Review Round 1 — per-product registration is required for dispatch).
 *
 * - Portfolio + Budget cluster (6) + Keyword family (9) live in
 *   `SP_V3_DIMENSION_MAP` (shared across SP/SB/SD campaign reports).
 * - Lead Gen-specific dims (3) live in `SD_V3_DIMENSION_MAP` — surface
 *   ONLY when Lead Gen campaigns exist; null-tolerant per AC #3 via
 *   Story 19.14 Round 3 Vuln 3 `Number.isFinite()` guard.
 *
 * This export is a DERIVED VIEW (not a separate source-of-truth) — entries
 * are filtered from `AMAZON_ADS_SP_DIMENSIONS` + `AMAZON_ADS_SD_DIMENSIONS`
 * by the 18 canonical key strings. Avoids duplication in
 * `AMAZON_ADS_DIMENSIONS` and keeps single-source-of-truth on per-product
 * maps. Tests against this export verify the 18 keys land correctly.
 */
export const AMAZON_ADS_STORY_35_7_DIMENSION_KEYS = [
  // Portfolio + Campaign Budget cluster (6)
  "portfolioId",
  "campaignBudgetAmount",
  "campaignBudgetCurrencyCode",
  "campaignRuleBasedBudgetAmount",
  "campaignApplicableBudgetRuleId",
  "campaignApplicableBudgetRuleName",
  // Keyword family (9)
  "keywordId",
  "keyword",
  "keywordType",
  "keywordBid",
  "targetingId",
  "targetingText",
  "searchTerm",
  "biddingStrategy",
  "campaignBudgetType",
  // SD Lead Gen-specific (3)
  "leadFormId",
  "leadFormType",
  "leadFormSchemaVersion",
] as const;

/**
 * Build the derived view with explicit dedup so a future story registering
 * the same key in both SP and SD dim maps doesn't produce a phantom
 * duplicate entry (would fail the `toHaveLength(18)` test but with a
 * misleading "off-by-one" error). The Map.set semantics keep the FIRST
 * occurrence per key — SP wins over SD when both define the same key.
 */
export const AMAZON_ADS_STORY_35_7_DIMENSIONS: DimensionDefinition[] = (() => {
  const allowed = new Set<string>(AMAZON_ADS_STORY_35_7_DIMENSION_KEYS);
  const byKey = new Map<string, DimensionDefinition>();
  for (const d of [...AMAZON_ADS_SP_DIMENSIONS, ...AMAZON_ADS_SD_DIMENSIONS]) {
    if (!allowed.has(d.key)) continue;
    if (!byKey.has(d.key)) byKey.set(d.key, d);
  }
  // Round 3 — fail loudly at module-load if any canonical key is missing
  // from BOTH per-product dim maps; prevents the "silent off-by-one"
  // mode where `toHaveLength(18)` fails with no indication of which key
  // went missing.
  if (byKey.size !== AMAZON_ADS_STORY_35_7_DIMENSION_KEYS.length) {
    const missing = AMAZON_ADS_STORY_35_7_DIMENSION_KEYS.filter(
      (k) => !byKey.has(k)
    );
    throw new Error(
      `AMAZON_ADS_STORY_35_7_DIMENSIONS: ${missing.length} canonical keys missing from per-product dim maps (SP/SD): ${missing.join(", ")}. Add the missing dim(s) to SP_V3_DIMENSION_MAP or SD_V3_DIMENSION_MAP.`
    );
  }
  return [...byKey.values()];
})();

/**
 * Story 19.16 AC #1 — 29 priority dimensions (8 SP + 4 SB + 4 SD + 13 DSP).
 * The 3 campaign* dimensions (campaignId/Name/Status) are NOT duplicated in
 * the SB/SD maps; they are sourced from the SP map and shared. DSP dimensions
 * are entirely separate (ORDER/LINE_ITEM/CREATIVE/siteName/etc.) and do not
 * overlap with sponsored-ads dimensions.
 *
 * Story 35.7 AC #7 — the 18 long-tail dims are routed via the per-product
 * dim maps (SP +15, SD +3) and surface here transitively via
 * `AMAZON_ADS_SP_DIMENSIONS` + `AMAZON_ADS_SD_DIMENSIONS`. No additional
 * spread needed — would duplicate.
 */
/**
 * Story 35.7 (Code Review Round 3) — dedup the umbrella by key. Per-product
 * dim maps now share 8 Portfolio + Budget cluster dims across SP/SB/SD
 * (they're `reportTypeId: "any"` so they surface in all 3 per-product
 * arrays). The umbrella exposes each dim ONCE; first-occurrence-wins
 * preserves SP-side labels when conflicts exist (mirrors `AMAZON_ADS_METRICS`
 * surface-ambiguity precedent).
 */
function dedupDimensionsByKey(
  arrays: DimensionDefinition[][]
): DimensionDefinition[] {
  const byKey = new Map<string, DimensionDefinition>();
  for (const arr of arrays) {
    for (const d of arr) {
      if (!byKey.has(d.key)) byKey.set(d.key, d);
    }
  }
  return [...byKey.values()];
}

export const AMAZON_ADS_DIMENSIONS: DimensionDefinition[] = dedupDimensionsByKey([
  AMAZON_ADS_SP_DIMENSIONS,
  AMAZON_ADS_SB_DIMENSIONS,
  AMAZON_ADS_SD_DIMENSIONS,
  AMAZON_ADS_DSP_DIMENSIONS,
  // Story 19.16.1 — Brand Store Engagement section-level dims.
  AMAZON_ADS_STORE_ANALYTICS_DIMENSIONS,
]);

/**
 * Story 19.16 — given a list of metric keys, group them by adProduct so the
 * dispatch in `fetchMetrics` can route to the correct per-product fetcher.
 * Returns the de-duplicated set of products that the request touches.
 *
 * **Surface ambiguity caveat:** several DSP metric keys (`impressions` /
 * `viewableImpressions` / `clickThroughs` / etc.) collide with SP/SB/SD keys.
 * `groupMetricsByAdProduct` resolves to the FIRST matching map in declared
 * priority order: SP → SB → SD → DSP. Callers that want unambiguous DSP
 * dispatch must use `ctx.requestedLevel === "amazon-dsp"` (the surface hint
 * defined in the adapter's `fetchMetrics` switch).
 */
export function groupMetricsByAdProduct(
  metricKeys: ReadonlyArray<string>
): AmazonAdsAdProduct[] {
  const products = new Set<AmazonAdsAdProduct>();
  for (const key of metricKeys) {
    if (SP_V3_COLUMN_MAP[key]) products.add("SPONSORED_PRODUCTS");
    else if (SB_V3_COLUMN_MAP[key]) products.add("SPONSORED_BRANDS");
    else if (SD_V3_COLUMN_MAP[key]) products.add("SPONSORED_DISPLAY");
    // Story 19.16.1 — DSP map extended to 6 sub-maps + STORE_ANALYTICS.
    else if (DSP_V3_COLUMN_MAP[key]) products.add("DSP");
    else if (DSP_FUNNEL_COLUMN_MAP[key]) products.add("DSP");
    else if (DSP_MASHUP_COLUMN_MAP[key]) products.add("DSP");
    else if (DSP_TECHNICAL_COLUMN_MAP[key]) products.add("DSP");
    else if (
      (AMAZON_ADS_DSP_AUDIENCE_FLOOR_METRICS as readonly string[]).includes(key)
    )
      products.add("DSP");
    else if (
      (AMAZON_ADS_DSP_AUDIO_VIDEO_FLOOR_METRICS as readonly string[]).includes(
        key
      )
    )
      products.add("DSP");
    else if (STORE_ANALYTICS_SECTION_LEVEL_COLUMN_MAP[key]) {
      // Brand Store Engagement metrics live on the Stores Analytics API,
      // NOT the DSP Reports API. They do NOT map to an AmazonAdsAdProduct;
      // a future epic adds a separate `STORE_ANALYTICS` dispatch surface.
      // For now we silently exclude them — the adapter routes via
      // `STORE_ANALYTICS_SECTION_LEVEL_COLUMN_MAP` directly when present.
      continue;
    }
    // Unknown keys are intentionally ignored here — they surface as warnings
    // when the per-product `buildColumnList` runs. The dispatch only needs
    // to know which products are touched.
  }
  return [...products];
}

// ============================================================================
// Internal helpers
// ============================================================================

/**
 * Story 35.7 AC #9 (Code Review Round 2) — per-isolate idempotency flag for
 * the BSE + DSP funnel-event audit logs. Both fire ONCE per Workers isolate
 * cold start when `validateConnection` is invoked; subsequent invocations
 * within the same isolate are no-ops. This keeps Grafana volume proportional
 * to deployment cadence (not to traffic) while ensuring at least one
 * forensic emission per deployment.
 *
 * Test seam: `resetAmazonAdsModuleAuditsForTests` lets test suites force the
 * flag back to `false` between tests so the emission is observable in
 * isolation.
 */
let amazonAdsModuleAuditsEmitted = false;

function emitAmazonAdsModuleAuditsOnce(): void {
  if (amazonAdsModuleAuditsEmitted) return;
  amazonAdsModuleAuditsEmitted = true;
  emitBseGridAuditLog();
  emitDspFunnelEventAuditLog();
}

/** Test seam — see `emitAmazonAdsModuleAuditsOnce` JSDoc. */
export function resetAmazonAdsModuleAuditsForTests(): void {
  amazonAdsModuleAuditsEmitted = false;
}

function adapterErrorCodeFromError(err: unknown): AdapterErrorCode {
  if (err instanceof AmazonAdsApiCallError) {
    return amazonAdsErrorTypeToAdapterCode(err.type);
  }
  const msg = err instanceof Error ? err.message : String(err);
  if (/timeout|fetch failed|ENOTFOUND|ECONNRESET/i.test(msg)) {
    return "network_error";
  }
  return "invalid_response";
}

// ============================================================================
// Story 19.17 — Fixture-mode helper (single KV key dispatches all 4 surfaces)
// ============================================================================

/** Story 19.17 — minimum AMAZON_ADS_FIXTURE_SCHEMA_VERSION the adapter accepts. */
const AMAZON_ADS_FIXTURE_MIN_SCHEMA_VERSION = "19.17.0";

/**
 * Compare two semver-style version strings ("MAJOR.MINOR.PATCH"). Returns
 * negative if a < b, zero if equal, positive if a > b. Tolerates fewer/extra
 * segments by treating missing segments as 0 and ignoring trailing pre-release
 * tags. Story 19.17 review-pass-1 fix: prior code used lexicographic string
 * compare which incorrectly judged "19.10.0" < "19.2.0".
 */
function compareSemverStrings(a: string, b: string): number {
  const partsA = a.split(/[.+-]/, 3).map((s) => Number(s) || 0);
  const partsB = b.split(/[.+-]/, 3).map((s) => Number(s) || 0);
  for (let i = 0; i < 3; i++) {
    const av = partsA[i] ?? 0;
    const bv = partsB[i] ?? 0;
    if (av !== bv) return av - bv;
  }
  return 0;
}

/** Story 19.17 — surface filter map (ctx.requestedLevel → _adProduct). */
const AMAZON_ADS_REQUESTED_LEVEL_TO_AD_PRODUCT: Record<
  string,
  "SP" | "SB" | "SD" | "DSP"
> = {
  "amazon-sp": "SP",
  "amazon-sb": "SB",
  "amazon-sd": "SD",
  "amazon-dsp": "DSP",
};

/**
 * Story 19.17 — short-circuits the production dispatch when `ctx.fixtures` is
 * `true`. Reads `fixture:amazon-ads:{clientSlug}` from KV and applies a
 * post-load `_adProduct` filter based on `ctx.requestedLevel`.
 *
 * **Critical invariants** (regression-tested in `amazon-ads.test.ts`):
 *   1. Runs BEFORE any production-path validation (lwaClientId / accountId /
 *      adProduct dispatch). Caller MUST short-circuit at the very top of
 *      `fetchMetrics()`.
 *   2. The 4-surface filter — single fixture KV key serves all 4 widget
 *      routes via `_adProduct` discriminator (Reddit/Snap/Pinterest's
 *      single-surface helper diverges here).
 *   3. `dataFreshnessLagHours` recomputes max-wins based on surfaces present
 *      after filtering: 1 (SP-only) / 3 (SB or SD present) / 12 (DSP present).
 *   4. NEVER writes to the production cache — read-only on `fixture:` keys.
 *      (Round 1 Senior Backend invariant — no `kv.put` on `amazon-ads:*` from
 *      this code path.)
 *   5. Unknown `ctx.requestedLevel` (typo / future enum value) throws a
 *      `TypeError` synchronously — silent fallback to "all 4 surfaces" would
 *      mask widget-config bugs. (Round 1 QA Test Architect invariant.)
 *
 * Mirrors `loadRedditAdsFixture` at `reddit-ads.ts:266-329` in structure.
 */
async function loadAmazonAdsFixture(
  ctx: AdapterContext,
  dateRange: { start: Date; end: Date }
): Promise<AdapterResult<AdapterMetrics>> {
  const env = ctx.env as unknown as Record<string, unknown>;
  const kv = env.KV as unknown;
  const db = env.DB as unknown;

  if (!kv || typeof (kv as { get?: unknown }).get !== "function") {
    return adapterErr({
      code: "permission_denied",
      message: "Missing KV binding — cannot load Amazon Ads demo fixture",
      retryable: false,
    });
  }
  if (!db) {
    return adapterErr({
      code: "permission_denied",
      message: "Missing DB binding — cannot resolve demo client slug",
      retryable: false,
    });
  }

  const { getFixtureFromKV, filterFixtureByDateRange } = await import(
    "../fixtures/kv-loader"
  );
  const { getClientSlug } = await import("../fixtures/demo-fetch");

  let clientSlug: string;
  try {
    clientSlug = await getClientSlug(
      db as Parameters<typeof getClientSlug>[0],
      ctx.tenantId,
      ctx.clientId
    );
  } catch (err) {
    return adapterErr({
      code: "account_not_found",
      message: `Amazon Ads demo client not found: ${err instanceof Error ? err.message : String(err)}`,
      retryable: false,
    });
  }

  const metrics = await getFixtureFromKV(
    kv as Parameters<typeof getFixtureFromKV>[0],
    "amazon-ads",
    clientSlug
  );

  if (metrics === null) {
    // Round 1 Customer Support — emit `amazon_ads_demo_fixture_missing`
    // structured log so support can replay the issue from Grafana
    // without source-code access (AC #21).
    console.warn(
      JSON.stringify({
        structured_log: "amazon_ads_demo_fixture_missing",
        tenantId: ctx.tenantId,
        integrationId: ctx.integrationId,
        clientSlug,
        kvKey: `fixture:amazon-ads:${clientSlug}`,
      })
    );
    return adapterErr({
      code: "account_not_found",
      message: `Amazon Ads demo fixture not yet uploaded for client ${clientSlug}; run pnpm fixtures:upload`,
      retryable: false,
    });
  }

  // Round 2 Pre-mortem B — schema version drift detection.
  // Use a semver-aware comparator (lexicographic compare is broken for
  // versions like "19.10.0" vs "19.2.0" — would treat "19.10.0" as < "19.2.0"
  // because '1' < '2' as a string).
  const fixtureVersion =
    (metrics.metadata?.fixtureSchemaVersion as string | undefined) ?? "0.0.0";
  if (compareSemverStrings(fixtureVersion, AMAZON_ADS_FIXTURE_MIN_SCHEMA_VERSION) < 0) {
    console.warn(
      JSON.stringify({
        structured_log: "amazon_ads_demo_fixture_stale_schema",
        tenantId: ctx.tenantId,
        integrationId: ctx.integrationId,
        clientSlug,
        fixtureSchemaVersion: fixtureVersion,
        minimumRequired: AMAZON_ADS_FIXTURE_MIN_SCHEMA_VERSION,
      })
    );
    // Non-fatal: still return the fixture so old fixtures continue to render.
  }

  // Validate ctx.requestedLevel — only accepts known Amazon Ads surface hints
  // OR undefined (Cross-Surface preset). Round 1 QA invariant.
  const requestedLevel = ctx.requestedLevel;
  if (
    requestedLevel !== undefined &&
    !(requestedLevel in AMAZON_ADS_REQUESTED_LEVEL_TO_AD_PRODUCT)
  ) {
    throw new TypeError(
      `loadAmazonAdsFixture: ctx.requestedLevel must be one of ` +
        `"amazon-sp" | "amazon-sb" | "amazon-sd" | "amazon-dsp" | undefined; ` +
        `got "${String(requestedLevel)}". Silent fallback to "all 4 surfaces" ` +
        `would mask widget-config bugs.`
    );
  }

  // Date-range filter via the existing kv-loader helper.
  const filtered = filterFixtureByDateRange(metrics, dateRange.start, dateRange.end);

  // Surface filter (Story 19.17 — Amazon-specific dispatch on _adProduct).
  // `requestedLevel` was validated to be either undefined or a valid key
  // above (TypeError thrown otherwise), so the lookup is total when defined.
  const surfaceFilter =
    requestedLevel === undefined
      ? null
      : AMAZON_ADS_REQUESTED_LEVEL_TO_AD_PRODUCT[requestedLevel]!;

  const surfaceFilteredRecords = surfaceFilter
    ? filtered.records.filter((r) => r._adProduct === surfaceFilter)
    : filtered.records;

  // Compute max-wins data freshness lag based on surfaces present.
  const surfacesReturned = new Set<string>();
  for (const r of surfaceFilteredRecords) {
    const ap = r._adProduct;
    if (typeof ap === "string") surfacesReturned.add(ap);
  }
  const hasDsp = surfacesReturned.has("DSP");
  const hasSbOrSd = surfacesReturned.has("SB") || surfacesReturned.has("SD");
  const dataFreshnessLagHours = hasDsp ? 12 : hasSbOrSd ? 3 : 1;

  // AC #15 — `amazon_ads_demo_fixture_loaded` structured log.
  console.log(
    JSON.stringify({
      structured_log: "amazon_ads_demo_fixture_loaded",
      tenantId: ctx.tenantId,
      integrationId: ctx.integrationId,
      clientSlug,
      requestedLevel: requestedLevel ?? "all",
      surfacesReturned: [...surfacesReturned].sort(),
      recordCount: surfaceFilteredRecords.length,
      dataFreshnessLagHours,
      fixtureNote: "Fixture data; does not reflect any real Amazon campaign.",
    })
  );

  return adapterOk({
    ...filtered,
    records: surfaceFilteredRecords,
    recordCount: surfaceFilteredRecords.length,
    metadata: {
      ...filtered.metadata,
      dataFreshnessLagHours,
    },
  });
}

// ============================================================================
// Adapter
// ============================================================================

export const amazonAdsAdapter: IntegrationAdapter = {
  platform: "amazon-ads",

  rateLimits: {
    // Amazon's documented per-token quota varies by endpoint family. The
    // /v2/profiles discovery endpoint and the SP/SB/SD/DSP report endpoints
    // each have their own quotas. Story 19.14 wires per-report-type quota
    // tracking; 19.13 uses a conservative default that won't trip rate limits
    // during OAuth-time discovery.
    requestsPerMinute: 60,
    retryAfterMs: 1000,
  },

  /**
   * Story 19.14 — Sponsored Products `fetchMetrics` implementation.
   *
   * Delegates to `runSpFetchMetrics` for the full async-only v3 reports
   * pipeline (POST → poll → download → parse → cache → return). Persists
   * every report submission to D1 BEFORE polling so the phantom-report reaper
   * Cron can cancel orphaned reports if the Worker dies mid-flight.
   *
   * SB/SD/DSP `reportTypeId` values land in Stories 19.15 / 19.16.
   */
  async fetchMetrics(
    ctx: AdapterContext,
    credentials: OAuthCredentials,
    dateRange: { start: Date; end: Date }
  ): Promise<AdapterResult<AdapterMetrics>> {
    // Story 19.17 — fixture-mode short-circuit (AC #9). MUST run BEFORE any
    // production-path validation so a demo workspace with no live LWA client
    // ID still renders fixture data without triggering `token_invalid`.
    const fixtures =
      (ctx as unknown as { fixtures?: boolean }).fixtures === true;
    if (fixtures) {
      return loadAmazonAdsFixture(ctx, dateRange);
    }

    const env = ctx.env as unknown as Record<string, unknown>;
    const lwaClientId =
      typeof env.AMAZON_ADS_CLIENT_ID === "string"
        ? env.AMAZON_ADS_CLIENT_ID
        : "";
    if (!lwaClientId) {
      return adapterErr({
        code: "token_invalid",
        message:
          "Missing AMAZON_ADS_CLIENT_ID env binding — cannot run Amazon Ads SP fetchMetrics",
        retryable: false,
      });
    }

    const accountId = ctx.accountId ?? "";
    if (!accountId) {
      return adapterErr({
        code: "account_not_found",
        message:
          "Amazon Ads SP fetchMetrics: ctx.accountId (primary profile ID from Story 19.13) is required",
        retryable: false,
      });
    }

    // The integration's region was stored at OAuth time per Story 19.13 AC #4.
    // Adapter contract doesn't expose `integration` directly — read it from
    // an optional `ctx.metadata.amazonAdsRegion` field; fallback to "NA" for
    // backwards-compat.
    const ctxAny = ctx as unknown as {
      readonly amazonAdsRegion?: AmazonAdsRegion;
    };
    const region: AmazonAdsRegion =
      typeof ctxAny.amazonAdsRegion === "string" &&
      (ctxAny.amazonAdsRegion === "NA" ||
        ctxAny.amazonAdsRegion === "EU" ||
        ctxAny.amazonAdsRegion === "FE")
        ? ctxAny.amazonAdsRegion
        : "NA";

    const dbBinding = ctx.env.DB as Parameters<typeof buildSpReportJobsApi>[0];
    const kvBinding = ctx.env.KV as Parameters<typeof buildSpKvApi>[0];
    const jobs = buildSpReportJobsApi(dbBinding);
    const kv = buildSpKvApi(kvBinding);

    // Story 19.15 AC #1 / Task 9.1 — dispatch on adProduct based on the
    // requested metrics. SP-only / SB-only / SD-only → routed to the matching
    // per-product fetcher. Mixed-product widgets are explicitly OUT-OF-SCOPE
    // (Story 19.6 PPC Unified Widget will lift this restriction) — return
    // `invalid_request` with a customer-facing message.
    //
    // **Surface hint (Round 3 review)**: when the widget knows it wants a
    // specific Amazon Ads surface (e.g. an SB-only "Branded Search" widget),
    // it can set `ctx.requestedLevel` to `"amazon-sb"` or `"amazon-sd"` to
    // override the heuristic resolution for shared keys (`impressions`,
    // `clicks`, `cost`, `sales14d`, etc. exist in multiple maps). Without the
    // hint, shared keys default to SP for backwards-compat with 19.14.
    const requestedMetrics = ctx.requestedColumns ?? [];
    const surfaceHint =
      ctx.requestedLevel === "amazon-sp"
        ? ("SPONSORED_PRODUCTS" as const)
        : ctx.requestedLevel === "amazon-sb"
          ? ("SPONSORED_BRANDS" as const)
          : ctx.requestedLevel === "amazon-sd"
            ? ("SPONSORED_DISPLAY" as const)
            : ctx.requestedLevel === "amazon-dsp"
              ? ("DSP" as const)
              : undefined;

    const adProductsRequested = groupMetricsByAdProduct(requestedMetrics);

    if (adProductsRequested.length > 1) {
      return adapterErr({
        code: "invalid_request",
        message:
          "Mixed-ad-product widgets are not yet supported. Pick one Amazon Ads surface (Sponsored Products, Sponsored Brands, Sponsored Display, or DSP) per widget. Story 19.6 (PPC Unified Widget) will lift this restriction.",
        retryable: false,
      });
    }

    // Surface-hint precedence: explicit ctx hint > metric-derived > SP default.
    const adProduct: AmazonAdsAdProduct =
      surfaceHint ?? adProductsRequested[0] ?? "SPONSORED_PRODUCTS";

    switch (adProduct) {
      case "SPONSORED_PRODUCTS":
        return runSpFetchMetrics({
          ctx,
          credentials,
          dateRange,
          integration: { accountId, region },
          lwaClientId,
          jobs,
          kv,
          metrics: requestedMetrics.length > 0 ? requestedMetrics : undefined,
        });
      case "SPONSORED_BRANDS":
        return runSbFetchMetrics({
          ctx,
          credentials,
          dateRange,
          integration: { accountId, region },
          lwaClientId,
          jobs,
          kv,
          metrics: requestedMetrics.length > 0 ? requestedMetrics : undefined,
        });
      case "SPONSORED_DISPLAY":
        return runSdFetchMetrics({
          ctx,
          credentials,
          dateRange,
          integration: { accountId, region },
          lwaClientId,
          jobs,
          kv,
          metrics: requestedMetrics.length > 0 ? requestedMetrics : undefined,
        });
      case "DSP": {
        // Story 19.16 — DSP requires the dspAccountId path parameter from an
        // agency-typed profile (NOT the sponsored-ads profileId from
        // ctx.accountId). The OAuth callback enrichment (AC #9) persists this
        // to `account_metadata.dspAdvertisers.dspAccountId`. The adapter reads
        // the JSON field via the optional ctx extension below; falls back to
        // ctx.accountId for back-compat with deployments that haven't run the
        // DSP enrichment yet (their request will likely 401 — see AC #6
        // dsp_not_enabled mapping).
        const ctxAnyDsp = ctx as unknown as {
          readonly amazonAdsDspAccountId?: string;
          readonly amazonAdsDspAdvertiserIds?: ReadonlyArray<string>;
        };
        const dspAccountId =
          typeof ctxAnyDsp.amazonAdsDspAccountId === "string" &&
          ctxAnyDsp.amazonAdsDspAccountId.length > 0
            ? ctxAnyDsp.amazonAdsDspAccountId
            : accountId;
        const dspAdvertiserIds =
          ctxAnyDsp.amazonAdsDspAdvertiserIds ?? [];
        return runDspFetchMetrics({
          ctx,
          credentials,
          dateRange,
          integration: {
            accountId: dspAccountId,
            region,
            advertiserIds:
              dspAdvertiserIds.length > 0 ? dspAdvertiserIds : undefined,
          },
          lwaClientId,
          jobs,
          kv,
          metrics: requestedMetrics.length > 0 ? requestedMetrics : undefined,
        });
      }
      default: {
        // Exhaustiveness gate — adding a 5th surface fails typecheck here.
        const _exhaustive: never = adProduct;
        return adapterErr({
          code: "invalid_request",
          message: `Unsupported Amazon Ads ad product: ${String(_exhaustive)}`,
          retryable: false,
        });
      }
    }
  },

  /**
   * Validates the Amazon Ads connection by re-running multi-region profile
   * discovery. Returns `adapterOk(true)` if at least one profile is
   * accessible across any region; otherwise maps to the appropriate adapter
   * code.
   *
   * If ALL 3 regions return 401/403 (full-account 401), this is a token
   * issue rather than per-region gating — the adapter routes through
   * `token_expired` so the cron action is `mark_revoked_notify_user`.
   */
  async validateConnection(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<boolean>> {
    // Story 35.7 AC #9 (Code Review Round 2) — emit the two module-load-style
    // forensic audit logs ONCE per Workers isolate, gated by per-isolate
    // module-level flags. `validateConnection` is the canonical first-call
    // entrypoint of the adapter; this avoids module-load-time auto-emission
    // (which pollutes test stdout AND fires on every isolate cold start) while
    // still guaranteeing the audit lands at least once per deployment.
    emitAmazonAdsModuleAuditsOnce();

    const env = ctx.env as unknown as Record<string, string | undefined>;
    const clientId = env.AMAZON_ADS_CLIENT_ID ?? "";
    if (!clientId) {
      return adapterErr({
        code: "token_invalid",
        message: "Missing AMAZON_ADS_CLIENT_ID env binding",
        retryable: false,
      });
    }

    try {
      const result = await fetchAmazonAdsProfilesAcrossRegions(
        credentials.accessToken,
        clientId
      );

      // All-3-regions-401 → token_expired (per AC #4). The adapter cannot
      // distinguish "user revoked" from "token expired" but both resolve to
      // the same cron action (mark_revoked_notify_user).
      if (
        result.totalProfileCount === 0 &&
        result.regionsWithoutAccess.length === 3
      ) {
        return adapterErr({
          code: "token_expired",
          message:
            "Amazon Ads validateConnection: all 3 regions returned 401/403 — token may be revoked",
          retryable: false,
        });
      }

      // All-3-regions-outage → provider_unavailable (transient). Cron action
      // is `transient_retry` rather than user notification — Amazon's data
      // API is having an outage, the customer's connection is fine.
      if (
        result.totalProfileCount === 0 &&
        result.regionsWithOutage.length === 3
      ) {
        return adapterErr({
          code: "provider_unavailable",
          message:
            "Amazon Ads validateConnection: all 3 regions returned transient errors (5xx/timeout) — provider outage",
          retryable: true,
        });
      }

      // Total-zero-profiles with mixed regional status (some OK, no profiles
      // anywhere) → user has no profiles in any accessible region. Surfaces
      // as `account_not_found` so the cron does not loop on a fundamentally
      // broken connection.
      if (result.totalProfileCount === 0) {
        return adapterErr({
          code: "account_not_found",
          message:
            "Amazon Ads validateConnection: no profiles accessible across any region",
          retryable: false,
        });
      }

      return adapterOk(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const code = adapterErrorCodeFromError(err);
      return adapterErr({
        code,
        message: sanitizeAmazonAdsErrorMessage(
          `Amazon Ads connection validation failed: ${message}`
        ),
        retryable: code === "rate_limited" || code === "network_error",
      });
    }
  },

  /**
   * Refreshes the Amazon Ads access token via the refresh_token grant.
   *
   * **LWA refresh tokens DO NOT rotate.** Adapter carries forward the existing
   * `credentials.refreshToken` unchanged. Defensive future-proof: if LWA
   * ever returns a `refresh_token` field in the response (e.g. in response
   * to the 180-day client-secret rotation policy), persist it.
   *
   * **AC #15 critical mapping invariant:**
   *   - LWA `invalid_grant` → `token_expired` (refresh token broken; user
   *     needs to re-auth)
   *   - LWA `invalid_client` → `token_invalid` (server-side secret wrong;
   *     dispatcher pre-flight surfaces as `deployment_misconfig` — cron
   *     action is `requeue_with_backoff`, NO user notification)
   *
   * Mismapping `invalid_client` to `token_expired` would mark every customer
   * connection as broken on a single server-side secret typo. This is the
   * single most important code review gate (Round 5 First Principles).
   *
   * **Does NOT set `refreshTokenExpiresAt`** — LWA refresh tokens are
   * permanent (column intentionally null per AC #2).
   */
  async refreshToken(
    ctx: AdapterRefreshContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<RefreshedTokens>> {
    if (!credentials.refreshToken) {
      return adapterErr({
        code: "token_invalid",
        message: "No refresh token available for Amazon Ads",
        retryable: false,
      });
    }

    const env = ctx.env as unknown as Record<string, string | undefined>;
    const clientId = env.AMAZON_ADS_CLIENT_ID ?? "";
    const clientSecret = env.AMAZON_ADS_CLIENT_SECRET ?? "";

    if (!clientId || !clientSecret) {
      return adapterErr({
        code: "token_invalid",
        message:
          "Missing AMAZON_ADS_CLIENT_ID or AMAZON_ADS_CLIENT_SECRET env bindings",
        retryable: false,
      });
    }

    try {
      const tokenResponse = await refreshAmazonAdsToken(
        credentials.refreshToken,
        clientId,
        clientSecret
      );

      // LWA non-rotating contract: response.refresh_token is omitted. We
      // carry forward the existing one. Defensive: if LWA ever defensively
      // returns a new refresh_token (e.g. forced rotation in 2027+), persist
      // it.
      const carriedRefreshToken =
        typeof tokenResponse.refresh_token === "string" &&
        tokenResponse.refresh_token.length > 0
          ? tokenResponse.refresh_token
          : credentials.refreshToken;

      return adapterOk({
        accessToken: tokenResponse.access_token,
        refreshToken: carriedRefreshToken,
        expiresAt: new Date(Date.now() + tokenResponse.expires_in * 1000),
        // refreshTokenExpiresAt deliberately omitted — LWA refresh tokens
        // are permanent (column intentionally null per AC #2).
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);

      // AC #15 critical mapping: distinguish invalid_grant (customer-actionable)
      // from invalid_client (server-side, no customer impact).
      if (err instanceof AmazonAdsTokenError) {
        if (err.oauthError === "invalid_grant") {
          return adapterErr({
            code: "token_expired",
            message: sanitizeAmazonAdsErrorMessage(
              `Amazon Ads token refresh failed (invalid_grant): ${message}`
            ),
            retryable: false,
          });
        }
        if (err.oauthError === "invalid_client") {
          // Structured-log alert (AC #15) — Grafana fires PagerDuty on
          // amazon_ads_invalid_client_alert.
          console.error(
            JSON.stringify({
              event: "amazon_ads_invalid_client_alert",
              severity: "critical",
              detail:
                "LWA returned invalid_client — server-side AMAZON_ADS_CLIENT_SECRET is incorrect (likely 180-day rotation typo or wrong-environment secret). Refresh tokens survive client-secret rotation; correct the secret and refreshes recover automatically.",
              timestamp: new Date().toISOString(),
            })
          );
          return adapterErr({
            code: "token_invalid",
            message: sanitizeAmazonAdsErrorMessage(
              `Amazon Ads deployment_misconfig (invalid_client): ${message}`
            ),
            retryable: true, // requeue_with_backoff per TD12/TD23
          });
        }
      }

      // Defensive fallback: regex-match on message for cases where the
      // typed error class wasn't propagated.
      const isInvalidGrant = /invalid_grant/i.test(message);
      const isInvalidClient = /invalid_client/i.test(message);
      if (isInvalidClient) {
        console.error(
          JSON.stringify({
            event: "amazon_ads_invalid_client_alert",
            severity: "critical",
            detail:
              "LWA returned invalid_client (regex fallback) — server-side secret incorrect.",
            timestamp: new Date().toISOString(),
          })
        );
        return adapterErr({
          code: "token_invalid",
          message: sanitizeAmazonAdsErrorMessage(
            `Amazon Ads deployment_misconfig (invalid_client): ${message}`
          ),
          retryable: true,
        });
      }

      return adapterErr({
        code: isInvalidGrant ? "token_expired" : "token_invalid",
        message: sanitizeAmazonAdsErrorMessage(
          `Amazon Ads token refresh failed: ${message}`
        ),
        retryable: false,
      });
    }
  },

  /**
   * Returns the flat list of profiles this OAuth grant can access, across
   * all 3 regions. Used by the OAuth callback flow to enumerate connected
   * profiles for the integration record.
   */
  async discoverAccounts(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<AccountInfo[]>> {
    const env = ctx.env as unknown as Record<string, string | undefined>;
    const clientId = env.AMAZON_ADS_CLIENT_ID ?? "";
    if (!clientId) {
      return adapterErr({
        code: "token_invalid",
        message: "Missing AMAZON_ADS_CLIENT_ID env binding",
        retryable: false,
      });
    }

    try {
      const result = await fetchAmazonAdsProfilesAcrossRegions(
        credentials.accessToken,
        clientId
      );
      const accounts: AccountInfo[] = result.profiles.map((p) => ({
        id: p.profileId,
        name:
          p.accountInfo.name.length > 0
            ? `${p.accountInfo.name} (${p.region}/${p.countryCode})`
            : `Profile ${p.profileId} (${p.region}/${p.countryCode})`,
        platform: "amazon-ads",
        metadata: {
          region: p.region,
          countryCode: p.countryCode,
          currencyCode: p.currencyCode,
          timezone: p.timezone,
          accountType: p.accountInfo.type,
          marketplaceStringId: p.accountInfo.marketplaceStringId,
          validPaymentMethod: p.accountInfo.validPaymentMethod,
          ...(p.accountInfo.subType !== undefined
            ? { accountSubType: p.accountInfo.subType }
            : {}),
        },
      }));
      return adapterOk(accounts);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return adapterErr({
        code: adapterErrorCodeFromError(err),
        message: sanitizeAmazonAdsErrorMessage(
          `Amazon Ads account discovery failed: ${message}`
        ),
        retryable: false,
      });
    }
  },

  getAvailableMetrics(): MetricDefinition[] {
    return AMAZON_ADS_METRICS;
  },

  getAvailableDimensions(): DimensionDefinition[] {
    return AMAZON_ADS_DIMENSIONS;
  },
};

/**
 * Story 19.14 AC #14 — Dry-run probe for the dashboard health-check path.
 *
 * Submits the smallest possible spCampaigns request (`startDate=endDate=yesterday`,
 * `columns=["impressions"]`, `groupBy=["campaign"]`) to validate the LWA grant
 * has report-creation scope, then immediately cancels the report so we don't
 * waste an in-flight slot on a connectivity test.
 *
 * **Forensics trail:** if the optional `recordProbe` callback is supplied, we
 * insert a row into `amazon_ads_report_jobs` with `status='cancelled_validation_probe'`
 * for forensics. Per AC #14, this lets a Customer Support rep paste the
 * `correlationId` into Grafana to trace dashboard health-check failures
 * end-to-end.
 *
 * On submission error, falls back to "no probe completed" — the customer
 * should not be marked unhealthy purely because reports are gated when profile
 * discovery still works (Story 19.13 path).
 *
 * Caller is responsible for the 15-min rate-limit gate via the integration
 * row's `last_validated_at` column (matches Story 19.13 pattern; the gate
 * lives in the route handler that invokes this helper, NOT in the adapter).
 */
export async function validateAmazonAdsSpDryRunProbe(args: {
  readonly accessToken: string;
  readonly lwaClientId: string;
  readonly profileId: string;
  readonly region: "NA" | "EU" | "FE";
  readonly fetchImpl?: typeof fetch;
  /**
   * Optional D1-row recorder. When supplied, the probe writes a forensics
   * row marked `cancelled_validation_probe` (AC #14). When absent, the probe
   * still works (validates report-creation scope) but no D1 trail is left.
   */
  readonly recordProbe?: (args: {
    readonly reportId: string;
    readonly correlationId: string;
  }) => Promise<void>;
}): Promise<
  | { readonly ok: true; readonly cancelledStatus: number; readonly correlationId: string }
  | { readonly ok: false; readonly reason: string }
> {
  const fetchImpl = args.fetchImpl ?? fetch;
  const correlationId = crypto.randomUUID();
  // Yesterday in UTC.
  const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000);
  const { body } = buildSpReportRequest({
    reportTypeId: "spCampaigns",
    startDate: yesterday,
    endDate: yesterday,
    metrics: ["impressions"],
    dimensions: ["campaignId"],
    requestHashShort: "dryrun00",
  });

  let submission;
  try {
    submission = await submitSponsoredProductsReport(
      {
        accessToken: args.accessToken,
        clientId: args.lwaClientId,
        profileId: args.profileId,
        region: args.region,
        body,
      },
      fetchImpl
    );
  } catch (err) {
    return {
      ok: false,
      reason: err instanceof Error ? err.message : String(err),
    };
  }

  // AC #14 forensics — record the probe BEFORE cancellation so the reaper
  // sees it if cancellation fails. `recordProbe` is fire-and-forget for
  // resilience: the probe itself is the truth source, the D1 row is just a
  // forensics hint.
  if (args.recordProbe) {
    try {
      await args.recordProbe({
        reportId: submission.reportId,
        correlationId,
      });
    } catch {
      // ignore — forensics is best-effort
    }
  }

  // Cancel immediately — don't burn an in-flight slot on a connectivity test.
  try {
    const cancelled = await cancelReportApi(
      {
        accessToken: args.accessToken,
        clientId: args.lwaClientId,
        profileId: args.profileId,
        region: args.region,
        reportId: submission.reportId,
      },
      fetchImpl
    );
    return { ok: true, cancelledStatus: cancelled.status, correlationId };
  } catch {
    // Submission succeeded but cancellation failed — still healthy from a
    // connectivity standpoint; the reaper Cron will reap the orphan within 30
    // minutes anyway.
    return {
      ok: true,
      cancelledStatus: 0, // sentinel — reap-on-next-tick
      correlationId,
    };
  }
}

// ============================================================================
// Story 19.15 — Multi-Surface Dry-Run Probe (AC #6, AC #14)
// ============================================================================

export type MultiSurfaceProbeErrorCode =
  | "token_expired"
  | "token_invalid"
  | "deployment_misconfig"
  | "provider_unavailable"
  | "permission_denied"
  | "api_not_enabled"
  | "brand_registry_required"
  | "dsp_not_enabled"
  | "rate_limited"
  | "invalid_response";

export type MultiSurfaceProbeOutcome =
  | { readonly ok: true; readonly cancelledStatus: number; readonly correlationId: string }
  | {
      readonly ok: false;
      readonly reason: string;
      readonly errorCode?: MultiSurfaceProbeErrorCode;
    };

export interface MultiSurfaceProbeResult {
  readonly sp: MultiSurfaceProbeOutcome;
  readonly sb: MultiSurfaceProbeOutcome;
  readonly sd: MultiSurfaceProbeOutcome;
  /**
   * Story 19.16 AC #14 — DSP probe outcome. Optional because customers
   * without DSP entitlement (most sponsored-ads customers) won't have a
   * dspAccountId; the probe is skipped and this field is `undefined`.
   * `evaluateMultiSurfaceProbe` treats `undefined` as "no DSP probe attempted"
   * — does NOT count as a failure.
   */
  readonly dsp?: MultiSurfaceProbeOutcome;
}

/**
 * Story 19.16 AC #14 — multi-surface health probe extended to 4 surfaces.
 * Submits a tiny dry-run report against EACH of SP / SB / SD AND optionally
 * DSP, immediately cancels each. Returns per-surface outcomes; the integration
 * is `healthy` if at least one probe succeeds; `unhealthy` only if ALL fail.
 *
 * **Priority order for elevated `unhealthy` reason** (Round 1 Multi-Brand-
 * Registry Agency PM walkthrough + Story 19.16 AC #6 extension):
 *   token_expired > deployment_misconfig > provider_unavailable >
 *   permission_denied > api_not_enabled > brand_registry_required >
 *   dsp_not_enabled
 *
 * `token_expired` is fixable by the user via reconnect (highest action
 * priority); `dsp_not_enabled` requires a customer business process AND most
 * customers don't have DSP at all — lowest priority "blame source". The
 * adapter MUST NOT mark a sponsored-ads-only customer's integration unhealthy
 * just because they don't have DSP.
 */
export async function probeAmazonAdsMultiSurface(args: {
  readonly accessToken: string;
  readonly lwaClientId: string;
  readonly profileId: string;
  readonly region: "NA" | "EU" | "FE";
  readonly fetchImpl?: typeof fetch;
  readonly recordProbe?: (probeArgs: {
    readonly reportId: string;
    readonly correlationId: string;
    readonly adProduct: AmazonAdsAdProduct;
  }) => Promise<void>;
  /**
   * Story 19.16 AC #14 — when supplied, the multi-surface probe also runs
   * a DSP probe against `/accounts/{dspAccountId}/dsp/reports/`. When omitted,
   * the DSP probe is SKIPPED (most customers don't have DSP entitlement).
   */
  readonly dspAccountId?: string;
  readonly dspAdvertiserIds?: ReadonlyArray<string>;
}): Promise<MultiSurfaceProbeResult> {
  const fetchImpl = args.fetchImpl ?? fetch;

  const sp = await probeOneSurface({
    adProduct: "SPONSORED_PRODUCTS",
    reportTypeId: "spCampaigns",
    metrics: ["impressions"],
    dimensions: ["campaignId"],
    args,
    fetchImpl,
  });
  const sb = await probeOneSurface({
    adProduct: "SPONSORED_BRANDS",
    reportTypeId: "sbCampaigns",
    metrics: ["impressions"],
    dimensions: ["campaignId"],
    args,
    fetchImpl,
  });
  const sd = await probeOneSurface({
    adProduct: "SPONSORED_DISPLAY",
    reportTypeId: "sdCampaigns",
    metrics: ["impressions"],
    dimensions: ["campaignId"],
    args,
    fetchImpl,
  });

  // Story 19.16 AC #14 — DSP probe is conditional on dspAccountId being
  // available. When omitted (most customers), result.dsp is undefined and
  // does NOT count as a failure in `evaluateMultiSurfaceProbe`.
  let dsp: MultiSurfaceProbeOutcome | undefined;
  if (args.dspAccountId && args.dspAccountId.length > 0) {
    dsp = await probeOneDspSurface({
      args: {
        ...args,
        dspAccountId: args.dspAccountId,
        dspAdvertiserIds: args.dspAdvertiserIds ?? [],
      },
      fetchImpl,
    });
  }

  return { sp, sb, sd, ...(dsp !== undefined ? { dsp } : {}) };
}

/**
 * Story 19.15 AC #14 — given a multi-surface probe result, determine
 * integration health and (if unhealthy) the highest-priority error code.
 */
export type MultiSurfaceProbeElevatedReason =
  | "token_expired"
  | "deployment_misconfig"
  | "provider_unavailable"
  | "permission_denied"
  | "api_not_enabled"
  | "brand_registry_required"
  /**
   * Story 19.16 AC #6 / AC #14 — DSP-specific gating. Lowest priority in the
   * elevated-unhealthy ladder because most customers don't have DSP. The
   * adapter MUST NOT mark a sponsored-ads-only customer's integration
   * unhealthy just because they don't have DSP.
   */
  | "dsp_not_enabled";

export interface MultiSurfaceProbeEvaluation {
  readonly health: "healthy" | "unhealthy";
  readonly elevatedReason?: MultiSurfaceProbeElevatedReason;
  readonly partialSurfaces: ReadonlyArray<{
    readonly adProduct: AmazonAdsAdProduct;
    readonly reason: string;
    readonly errorCode?: string;
  }>;
}

export function evaluateMultiSurfaceProbe(
  result: MultiSurfaceProbeResult
): MultiSurfaceProbeEvaluation {
  const surfaces: Array<[AmazonAdsAdProduct, MultiSurfaceProbeOutcome]> = [
    ["SPONSORED_PRODUCTS", result.sp],
    ["SPONSORED_BRANDS", result.sb],
    ["SPONSORED_DISPLAY", result.sd],
  ];
  // Story 19.16 AC #14 — include DSP outcome only if probed.
  if (result.dsp !== undefined) {
    surfaces.push(["DSP", result.dsp]);
  }

  const partials: Array<{
    adProduct: AmazonAdsAdProduct;
    reason: string;
    errorCode?: string;
  }> = [];
  let allFailed = true;
  for (const [product, outcome] of surfaces) {
    if (outcome.ok) {
      allFailed = false;
    } else {
      partials.push({
        adProduct: product,
        reason: outcome.reason,
        ...(outcome.errorCode !== undefined
          ? { errorCode: outcome.errorCode }
          : {}),
      });
      // Emit partial-surface log per AC #14.
      console.log(
        JSON.stringify({
          event: "amazon_ads_partial_surface_unavailable",
          adProduct: product,
          failedProduct: product,
          reason: outcome.errorCode ?? "unknown",
          timestamp: new Date().toISOString(),
        })
      );
    }
  }

  // Story 19.15 AC #14 — priority-elevated unhealthy reason. The order MATTERS:
  //   token_expired > deployment_misconfig > provider_unavailable >
  //   permission_denied > api_not_enabled > brand_registry_required.
  //
  // `token_expired` is fixable by the user via reconnect (highest action
  // priority); `brand_registry_required` requires customer business action and
  // is the lowest "blame source". `deployment_misconfig` is the probe-mapped
  // alias for the `token_invalid` Amazon-side error (per `probeOneSurface`).
  const PRIORITY: ReadonlyArray<MultiSurfaceProbeElevatedReason> = [
    "token_expired",
    "deployment_misconfig",
    "provider_unavailable",
    "permission_denied",
    "api_not_enabled",
    "brand_registry_required",
    "dsp_not_enabled",
  ] as const;

  if (allFailed) {
    let elevated: MultiSurfaceProbeElevatedReason | undefined;
    for (const candidate of PRIORITY) {
      for (const [, outcome] of surfaces) {
        if (!outcome.ok && outcome.errorCode === candidate) {
          elevated = candidate;
          break;
        }
      }
      if (elevated) break;
    }
    return {
      health: "unhealthy",
      ...(elevated !== undefined ? { elevatedReason: elevated } : {}),
      partialSurfaces: partials,
    };
  }

  return { health: "healthy", partialSurfaces: partials };
}

interface ProbeOneSurfaceArgs {
  readonly adProduct: AmazonAdsAdProduct;
  readonly reportTypeId: "spCampaigns" | "sbCampaigns" | "sdCampaigns";
  readonly metrics: ReadonlyArray<string>;
  readonly dimensions: ReadonlyArray<string>;
  readonly args: {
    readonly accessToken: string;
    readonly lwaClientId: string;
    readonly profileId: string;
    readonly region: "NA" | "EU" | "FE";
    readonly recordProbe?: (probeArgs: {
      readonly reportId: string;
      readonly correlationId: string;
      readonly adProduct: AmazonAdsAdProduct;
    }) => Promise<void>;
  };
  readonly fetchImpl: typeof fetch;
}

interface ProbeOneDspSurfaceArgs {
  readonly args: {
    readonly accessToken: string;
    readonly lwaClientId: string;
    readonly profileId: string;
    readonly region: "NA" | "EU" | "FE";
    readonly dspAccountId: string;
    readonly dspAdvertiserIds: ReadonlyArray<string>;
    readonly recordProbe?: (probeArgs: {
      readonly reportId: string;
      readonly correlationId: string;
      readonly adProduct: AmazonAdsAdProduct;
    }) => Promise<void>;
  };
  readonly fetchImpl: typeof fetch;
}

/**
 * Story 19.16 AC #14 — DSP dry-run probe. Posts the smallest possible DSP
 * report (`type: "CAMPAIGN"`, single dimension/metric, yesterday-only date
 * range) and immediately cancels it (the cancel hits the SP-shape endpoint
 * because DSP doesn't have a cancel endpoint per the Python SDK docs — but
 * Amazon will reap the IN_PROGRESS report on its own once the budget burns).
 *
 * Returns `dsp_not_enabled` when the customer's DSP entitlement is missing
 * (4xx body matching Round 19.16 AC #6 patterns) — the integration stays
 * `healthy` overall via `evaluateMultiSurfaceProbe`.
 */
async function probeOneDspSurface(
  pArgs: ProbeOneDspSurfaceArgs
): Promise<MultiSurfaceProbeOutcome> {
  const correlationId = crypto.randomUUID();
  const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000);
  const { body } = buildAmazonAdsReportRequest({
    adProduct: "DSP",
    reportTypeId: "DSP_CAMPAIGN",
    startDate: yesterday,
    endDate: yesterday,
    metrics: ["impressions"],
    dimensions: ["ORDER"],
    ...(pArgs.args.dspAdvertiserIds.length > 0
      ? { advertiserIds: pArgs.args.dspAdvertiserIds }
      : {}),
  });

  let submission;
  try {
    submission = await submitAmazonAdsReport(
      {
        accessToken: pArgs.args.accessToken,
        clientId: pArgs.args.lwaClientId,
        profileId: pArgs.args.dspAccountId,
        region: pArgs.args.region,
        body,
      },
      pArgs.fetchImpl
    );
  } catch (err) {
    if (err instanceof SpReportApiError) {
      const parsed = parseAmazonAdsReportApiError(err, "DSP");
      const mapped =
        parsed.code === "token_invalid" ? "deployment_misconfig" : parsed.code;
      return {
        ok: false,
        reason: err.message,
        errorCode: mapped,
      };
    }
    return {
      ok: false,
      reason: err instanceof Error ? err.message : String(err),
    };
  }

  if (pArgs.args.recordProbe) {
    try {
      await pArgs.args.recordProbe({
        reportId: submission.reportId,
        correlationId,
        adProduct: "DSP",
      });
    } catch {
      // forensics is best-effort
    }
  }

  // DSP doesn't expose a cancel endpoint; rely on Amazon's internal reaper.
  // Return ok with a sentinel cancelledStatus = -1 so callers can distinguish
  // DSP probe success (no cancel) from SP/SB/SD success (real cancel status).
  return { ok: true, cancelledStatus: -1, correlationId };
}

async function probeOneSurface(
  pArgs: ProbeOneSurfaceArgs
): Promise<MultiSurfaceProbeOutcome> {
  const correlationId = crypto.randomUUID();
  const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000);
  const { body } = buildAmazonAdsReportRequest({
    adProduct: pArgs.adProduct,
    reportTypeId: pArgs.reportTypeId,
    startDate: yesterday,
    endDate: yesterday,
    metrics: pArgs.metrics,
    dimensions: pArgs.dimensions,
    requestHashShort: "probe000",
  });

  let submission;
  try {
    submission = await submitAmazonAdsReport(
      {
        accessToken: pArgs.args.accessToken,
        clientId: pArgs.args.lwaClientId,
        profileId: pArgs.args.profileId,
        region: pArgs.args.region,
        body,
      },
      pArgs.fetchImpl
    );
  } catch (err) {
    if (err instanceof SpReportApiError) {
      const parsed = parseAmazonAdsReportApiError(err, pArgs.adProduct);
      // Map invalid_client → deployment_misconfig per Story 19.13 AC #15.
      const mapped =
        parsed.code === "token_invalid" ? "deployment_misconfig" : parsed.code;
      return {
        ok: false,
        reason: err.message,
        errorCode: mapped,
      };
    }
    return {
      ok: false,
      reason: err instanceof Error ? err.message : String(err),
    };
  }

  if (pArgs.args.recordProbe) {
    try {
      await pArgs.args.recordProbe({
        reportId: submission.reportId,
        correlationId,
        adProduct: pArgs.adProduct,
      });
    } catch {
      // forensics is best-effort
    }
  }

  try {
    const cancelled = await cancelReportApi(
      {
        accessToken: pArgs.args.accessToken,
        clientId: pArgs.args.lwaClientId,
        profileId: pArgs.args.profileId,
        region: pArgs.args.region,
        reportId: submission.reportId,
      },
      pArgs.fetchImpl
    );
    return { ok: true, cancelledStatus: cancelled.status, correlationId };
  } catch {
    return {
      ok: true,
      cancelledStatus: 0,
      correlationId,
    };
  }
}
